import React from 'react'
import { Tag, Icon, Empty, Spin } from 'antd'
import moment from 'moment'
import numeral from 'numeral'

const fmtPrice = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '-'
  return numeral(v).format('0,0.00')
}

const fmtDateTime = (v) => {
  if (!v) return '-'
  return moment.utc(v).utcOffset(7).format('YYYY-MM-DD HH:mm')
}

const getChange = (current, closing) => {
  const c = Number(current)
  const cl = Number(closing)
  if (!isFinite(c) || !isFinite(cl) || cl === 0) return null
  return ((c - cl) / Math.abs(cl)) * 100
}

const dirOf = (change) => {
  if (change == null) return 'flat'
  if (change > 0) return 'up'
  if (change < 0) return 'down'
  return 'flat'
}

const COLOR = {
  up: { fg: '#52c41a', bg: 'rgba(82,196,26,0.08)', border: 'rgba(82,196,26,0.25)' },
  down: { fg: '#f5222d', bg: 'rgba(245,34,45,0.08)', border: 'rgba(245,34,45,0.25)' },
  flat: { fg: '#8c8c8c', bg: 'rgba(140,140,140,0.06)', border: 'rgba(140,140,140,0.2)' }
}

const STATUS_COLOR = {
  SUCCESS: 'green',
  FAILED: 'red',
  BLOCKED: 'orange',
  TIMEOUT: 'default'
}

const formatPair = (currency, unit) => {
  const parts = [currency, unit].filter(Boolean)
  return parts.length ? parts.join('/') : ''
}

const PriceCard = ({ record, onClick }) => {
  // Primary = target (converted) only when BOTH ends exist; otherwise fall back
  // to source for both. Matches the % logic in Discord's `change_pct` so the
  // headline price + badge stay consistent with what gets posted to Discord.
  const hasTargetPair =
    record.target_current_price != null &&
    record.target_closing_price != null
  const primaryCurrent = hasTargetPair
    ? record.target_current_price
    : record.current_price
  const primaryClosing = hasTargetPair
    ? record.target_closing_price
    : record.closing_price != null
      ? record.closing_price
      : record.previous_price
  const primaryPair = hasTargetPair
    ? formatPair(record.target_currency, record.target_unit)
    : formatPair(record.source_currency, record.source_unit)
  const secondaryCurrent = hasTargetPair ? record.current_price : null
  const secondaryPair = hasTargetPair
    ? formatPair(record.source_currency, record.source_unit)
    : ''

  const change = getChange(primaryCurrent, primaryClosing)
  const dir = dirOf(change)
  const c = COLOR[dir]
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '◆'
  const status = record.last_status
    ? String(record.last_status).toUpperCase()
    : null

  return (
    <div
      onClick={() => onClick(record)}
      style={{
        background: '#fff',
        border: '1px solid #f0f0f0',
        borderRadius: 10,
        padding: '14px 16px',
        cursor: 'pointer',
        transition: 'box-shadow .2s, border-color .2s, transform .2s',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.08)'
        e.currentTarget.style.borderColor = c.border
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none'
        e.currentTarget.style.borderColor = '#f0f0f0'
      }}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: c.fg,
          opacity: 0.6
        }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 10
        }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={record.name}>
            {record.name}
          </div>
          {record.name_en && (
            <div
              style={{
                fontSize: 11,
                color: '#8c8c8c',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={record.name_en}>
              {record.name_en}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '3px 8px',
            borderRadius: 6,
            background: c.bg,
            border: '1px solid ' + c.border,
            color: c.fg,
            fontFamily: 'monospace',
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}>
          {arrow} {change != null ? numeral(Math.abs(change)).format('0,0.00') + '%' : '—'}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: c.fg
          }}>
            {fmtPrice(primaryCurrent)}
        </span>
        {primaryPair && (
          <span style={{ fontSize: 11, color: '#8c8c8c' }}>{primaryPair}</span>
        )}
      </div>

      {secondaryCurrent != null && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
          <span style={{ fontFamily: 'monospace' }}>
            {fmtPrice(secondaryCurrent)}
          </span>
          {secondaryPair && (
            <span style={{ marginLeft: 4 }}>{secondaryPair}</span>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: 12,
          paddingTop: 10,
          borderTop: '1px solid #fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap'
        }}>
        <span style={{ fontSize: 11, color: '#8c8c8c' }}>
          <Icon type="clock-circle" style={{ marginRight: 4 }} />
          {fmtDateTime(record.last_fetched_at)}
        </span>
        {status && (
          <Tag
            color={STATUS_COLOR[status] || 'default'}
            style={{ marginRight: 0, fontSize: 10 }}>
            {status}
          </Tag>
        )}
      </div>
    </div>
  )
}

const PriceCardGrid = ({ items, loading, onCardClick }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin />
      </div>
    )
  }
  if (!items || !items.length) {
    return <Empty description="ไม่มีรายการ" />
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 12
      }}>
      {items.map((record) => (
        <PriceCard key={record.id} record={record} onClick={onCardClick} />
      ))}
    </div>
  )
}

export default PriceCardGrid
